import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { StatCard, fmtDate } from "./ui";

export default function CommandDashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cc/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
  }, []);

  if (d === null) return <div className="text-muted-foreground">Loading…</div>;
  if (d === false) return <div className="text-red-300">Failed to load dashboard.</div>;

  return (
    <div className="space-y-10" data-testid="command-dashboard">
      <div>
        <div className="overline text-primary">DAFARGOS HOLDINGS LLC</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">Master Command Center</h1>
      </div>

      {d.alerts.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5" data-testid="attention-panel">
          <div className="flex items-center gap-2 text-amber-300 font-medium mb-3">
            <Warning size={20} weight="fill" /> Needs your attention
          </div>
          <ul className="space-y-1.5 text-sm text-amber-100/90">
            {d.alerts.map((a, i) => <li key={i}>• {a}</li>)}
          </ul>
        </div>
      )}

      <section>
        <h2 className="text-lg font-medium mb-4">Clients</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="stat-clients-pending" label="Pending approval" value={d.clients.pending || 0} accent={(d.clients.pending || 0) > 0} onClick={() => navigate("/command/clients?tab=pending")} />
          <StatCard testid="stat-clients-total" label="Total clients" value={d.clients.total} onClick={() => navigate("/command/clients")} />
          <StatCard testid="stat-clients-active" label="Active" value={d.clients.active} onClick={() => navigate("/command/clients")} />
          <StatCard testid="stat-clients-attention" label="Need attention" value={d.clients.needs_attention} onClick={() => navigate("/command/clients")} />
          <StatCard testid="stat-clients-mix" label="Individual / Business" value={`${d.clients.individual} / ${d.clients.business}`} sub={`${d.clients.both} both`} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-4">Businesses</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="stat-companies-total" label="Total companies" value={d.companies.total} accent onClick={() => navigate("/command/companies")} />
          <StatCard testid="stat-companies-active" label="Active" value={d.companies.active} onClick={() => navigate("/command/companies")} />
          <StatCard testid="stat-companies-forming" label="Being formed" value={d.companies.forming} onClick={() => navigate("/command/companies")} />
          <StatCard testid="stat-companies-mine" label="My companies" value={d.companies.mine} onClick={() => navigate("/command/companies")} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-4">Tasks</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="stat-tasks-today" label="Due today" value={d.tasks.due_today} accent onClick={() => navigate("/command/tasks")} />
          <StatCard testid="stat-tasks-week" label="Due this week" value={d.tasks.due_this_week} onClick={() => navigate("/command/tasks")} />
          <StatCard testid="stat-tasks-overdue" label="Overdue" value={d.tasks.overdue} onClick={() => navigate("/command/tasks")} />
          <StatCard testid="stat-tasks-upcoming" label="Upcoming" value={d.tasks.upcoming} onClick={() => navigate("/command/tasks")} />
        </div>
      </section>

      {d.payments && (
        <section>
          <h2 className="text-lg font-medium mb-4">Billing</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testid="stat-pay-outstanding" label="Outstanding" value={`$${Number(d.payments.outstanding || 0).toLocaleString()}`} accent={(d.payments.outstanding || 0) > 0} onClick={() => navigate("/command/billing")} />
            <StatCard testid="stat-pay-collected" label="Collected" value={`$${Number(d.payments.paid || 0).toLocaleString()}`} onClick={() => navigate("/command/billing")} />
            <StatCard testid="stat-pay-overdue" label="Payments overdue" value={d.payments.overdue || 0} onClick={() => navigate("/command/billing")} />
            <StatCard testid="stat-bills-monthly" label="Bills / month" value={`$${Number(d.bills?.monthly_total || 0).toLocaleString()}`} onClick={() => navigate("/command/billing")} />
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-medium mb-4">Documents</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="stat-docs-total" label="Total documents" value={d.documents.total} accent onClick={() => navigate("/command/documents")} />
          <StatCard testid="stat-docs-expiring" label="Expiring soon" value={d.documents.expiring} onClick={() => navigate("/command/documents")} />
        </div>
        {d.documents.recent.length > 0 && (
          <div className="mt-4 rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5">
            {d.documents.recent.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="truncate">{doc.name}</span>
                <span className="text-muted-foreground shrink-0 ml-4">{fmtDate(doc.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
